/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/
\* =========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import ListSubheader from '@mui/material/ListSubheader';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const ROW_H = 40;
const LIST_H = 320;

const initFind = {
  product_ids: [],
  cat_ids: [],
  warehouse: [],
  date_start: Func.dateFirstMonth(),
  date_stop: Func.dateEndMonth(),
  page: 1,
  perPage: 10,
};

const pickedIds = (ids, all) => {
  if (!ids.length || (all.length > 0 && ids.length === all.length)) return '';
  return ids.join(',');
};

const filterFieldSx = {
  '& .MuiInputBase-root': { height: 45 },
  '& .MuiInputBase-input, & .MuiSelect-select': {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputAdornment-root': { whiteSpace: 'nowrap', maxHeight: 'none' },
};

const headSx = {
  fontWeight: 600,
  textAlign: 'center',
  border: '1px solid #e0e0e0',
  py: 0.75,
  px: 1,
  lineHeight: 1.3,
};

const qty = (value) => {
  const num = Number(value);
  if (!num) return '';
  return Func.numberDisplay(num);
};

function RpProductMovement() {
  const mod = 'rp_product_movement';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [urlExcel, setUrlExcel] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [codeSearch, setCodeSearch] = useState('');
  const [openProducts, setOpenProducts] = useState(false);
  const [listScroll, setListScroll] = useState(0);
  const productAnchor = useRef(null);
  const listRef = useRef(null);
  const requestNo = useRef(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const pool = useMemo(() => {
    if (!find.cat_ids.length || find.cat_ids.length === categories.length) return products;
    return products.filter((item) =>
      find.cat_ids.some((row) => String(row) === String(item.cat_id))
    );
  }, [products, categories.length, find.cat_ids]);

  const choices = useMemo(() => {
    const query = codeSearch.trim().toLowerCase();
    if (!query) return pool;
    return pool.filter((item) => (item.code || '').toLowerCase().includes(query));
  }, [pool, codeSearch]);

  const listStart = Math.max(0, Math.floor(listScroll / ROW_H) - 8);
  const listEnd = Math.min(choices.length, listStart + Math.ceil(LIST_H / ROW_H) + 16);

  const productLabel = () => {
    if (!find.product_ids.length || find.product_ids.length === pool.length)
      return t('element.all');
    return products
      .filter((item) => find.product_ids.some((row) => String(row) === String(item.id)))
      .map((item) => item.code)
      .join(', ');
  };

  const view = (data, quiet = false) => {
    const list =
      !data.cat_ids.length || data.cat_ids.length === categories.length
        ? products
        : products.filter((item) =>
            data.cat_ids.some((row) => String(row) === String(item.cat_id))
          );
    const ticket = requestNo.current + 1;
    requestNo.current = ticket;
    setFind(data);
    closeShow();
    if (!quiet) setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      product_id: pickedIds(data.product_ids || [], list),
      cat: pickedIds(data.cat_ids || [], categories),
      warehouse: data.warehouse || [],
      date_start: data.date_start,
      date_stop: data.date_stop,
      page: data.page,
      per_page: data.perPage,
    })
      .then((result) => {
        if (ticket !== requestNo.current) return;
        if (result.status === 'ok') {
          setRecord(result.data || []);
          setPageTotal(result.total_pages);
          setPageNo(result.page);
        }
        setLoading(false);
      })
      .catch(() => {
        if (ticket !== requestNo.current) return;
        setLoading(false);
      });
  };

  const toggleProduct = (rowId) => {
    const picked = find.product_ids.some((row) => String(row) === String(rowId));
    view({
      ...find,
      product_ids: picked
        ? find.product_ids.filter((row) => String(row) !== String(rowId))
        : [...find.product_ids, rowId],
      page: 1,
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateFormat(start.toDate()),
      date_stop: Func.dateFormat(stop.toDate()),
      page: 1,
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('product_id', pickedIds(find.product_ids, pool));
    params.append('cat', pickedIds(find.cat_ids, categories));
    (find.warehouse || []).forEach((row) => params.append('warehouse[]', row));
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, { lang: app.lang }).then((result) => {
      if (result.status === 'ok') {
        setProducts(result.products || []);
        setCategories(result.categories || []);
        setWarehouses(result.warehouse || []);
        setUrlExcel(result.excel);
        view({
          ...find,
          warehouse: (result.warehouse || []).map((item) => Number(item.id)),
        });
      } else {
        Func.exitApp();
      }
    });
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  const cell = (value, align = 'right') => (
    <TableCell align={align} sx={{ border: '1px solid #eee', whiteSpace: 'nowrap' }}>
      {qty(value)}
    </TableCell>
  );

  return (
    <MDBox>
      <Navbar route={[t('menu.report_account'), t('menu.rp_product_movement')]} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={4}>
                  <MDBox ref={productAnchor}>
                    <MDInput
                      fullWidth
                      value={productLabel()}
                      onClick={() => setOpenProducts(true)}
                      sx={filterFieldSx}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position='start'>
                            {t('attr.product_code')} :
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MDBox>
                  <Menu
                    anchorEl={productAnchor.current}
                    open={openProducts}
                    onClose={() => {
                      setOpenProducts(false);
                      setCodeSearch('');
                    }}
                    disableAutoFocusItem
                    PaperProps={{
                      style: {
                        maxHeight: 480,
                        width: productAnchor.current
                          ? productAnchor.current.clientWidth
                          : undefined,
                      },
                    }}
                  >
                    <ListSubheader
                      sx={{ bgcolor: 'background.paper' }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <MDInput
                        autoFocus
                        fullWidth
                        placeholder={t('element.search')}
                        value={codeSearch}
                        onChange={(e) => {
                          setCodeSearch(e.target.value);
                          setListScroll(0);
                          if (listRef.current) listRef.current.scrollTop = 0;
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ListSubheader>
                    <MenuItem
                      onClick={() => {
                        const allSelected =
                          pool.length > 0 && find.product_ids.length === pool.length;
                        view({
                          ...find,
                          product_ids: allSelected ? [] : pool.map((item) => item.id),
                          page: 1,
                        });
                      }}
                    >
                      <Checkbox
                        checked={pool.length > 0 && find.product_ids.length === pool.length}
                      />
                      {t('element.all')}
                    </MenuItem>
                    <div
                      ref={listRef}
                      style={{
                        height: Math.min(LIST_H, Math.max(choices.length, 1) * ROW_H),
                        overflowY: 'auto',
                      }}
                      onScroll={(e) => setListScroll(e.target.scrollTop)}
                    >
                      <div style={{ height: choices.length * ROW_H, position: 'relative' }}>
                        {choices.slice(listStart, listEnd).map((item, index) => (
                          <MenuItem
                            key={item.id}
                            style={{
                              position: 'absolute',
                              top: (listStart + index) * ROW_H,
                              height: ROW_H,
                              width: '100%',
                            }}
                            onClick={() => toggleProduct(item.id)}
                          >
                            <Checkbox
                              checked={find.product_ids.some(
                                (row) => String(row) === String(item.id)
                              )}
                            />
                            {item.code}
                          </MenuItem>
                        ))}
                      </div>
                    </div>
                  </Menu>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    select
                    fullWidth
                    sx={filterFieldSx}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => {
                        if (!selected.length || selected.length === categories.length)
                          return t('element.all');
                        return categories
                          .filter((item) => selected.includes(Number(item.id)))
                          .map((item) => item.name)
                          .join(', ');
                      },
                    }}
                    value={find.cat_ids}
                    onChange={(e) => {
                      const { value } = e.target;
                      const ids = (Array.isArray(value) ? value : [value])
                        .map(Number)
                        .filter((row) => row > 0);
                      if (value.includes('ALL')) {
                        const allSelected =
                          categories.length > 0 && find.cat_ids.length === categories.length;
                        view({
                          ...find,
                          cat_ids: allSelected ? [] : categories.map((item) => Number(item.id)),
                          product_ids: [],
                          page: 1,
                        });
                      } else {
                        view({ ...find, cat_ids: ids, product_ids: [], page: 1 });
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          {t('attr.product_category')} :
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value='ALL'>
                      <Checkbox
                        checked={categories.length > 0 && find.cat_ids.length === categories.length}
                      />
                      {t('element.all')}
                    </MenuItem>
                    {categories.map((item) => (
                      <MenuItem key={item.id} value={Number(item.id)}>
                        <Checkbox checked={find.cat_ids.includes(Number(item.id))} />
                        {item.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    select
                    fullWidth
                    sx={filterFieldSx}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => {
                        if (warehouses.length > 0 && selected.length === warehouses.length)
                          return t('element.all');
                        if (!selected.length) return t('element.please_select');
                        return warehouses
                          .filter((item) => selected.includes(Number(item.id)))
                          .map((item) => item.name)
                          .join(', ');
                      },
                    }}
                    value={find.warehouse}
                    onChange={(e) => {
                      const { value } = e.target;
                      const ids = (Array.isArray(value) ? value : [value])
                        .map(Number)
                        .filter((row) => row > 0);
                      if (value.includes('ALL')) {
                        const allSelected =
                          warehouses.length > 0 && find.warehouse.length === warehouses.length;
                        view({
                          ...find,
                          warehouse: allSelected ? [] : warehouses.map((item) => Number(item.id)),
                          page: 1,
                        });
                      } else {
                        view({ ...find, warehouse: ids, page: 1 });
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>{t('attr.warehouse')} :</InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value='ALL'>
                      <Checkbox
                        checked={
                          warehouses.length > 0 && find.warehouse.length === warehouses.length
                        }
                      />
                      {t('element.all')}
                    </MenuItem>
                    {warehouses.map((item) => (
                      <MenuItem key={item.id} value={Number(item.id)}>
                        <Checkbox checked={find.warehouse.includes(Number(item.id))} />
                        {item.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox sx={filterFieldSx}>
                    <MDDateRange
                      dateOnly
                      dateStart={find.date_start}
                      dateStop={find.date_stop}
                      callBack={setDateRange}
                      closeDate={() =>
                        view({
                          ...find,
                          date_start: Func.dateFirstMonth(),
                          date_stop: Func.dateEndMonth(),
                          page: 1,
                        })
                      }
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4} sx={{ ml: { md: 'auto' } }}>
                  <MDButton
                    variant='gradient'
                    color='success'
                    fullWidth
                    onClick={excel}
                    sx={{ height: 45 }}
                  >
                    <Icon>table_view</Icon>&nbsp;Excel
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 1480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={headSx}>
                      วันที่
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      เลขที่เอกสาร
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      รหัสสินค้า
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      ชื่อสินค้า
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      หน่วย
                    </TableCell>
                    <TableCell colSpan={3} sx={{ ...headSx, bgcolor: '#e8f5e9' }}>
                      รับเข้า
                    </TableCell>
                    <TableCell colSpan={5} sx={{ ...headSx, bgcolor: '#fff3e0' }}>
                      จ่าย/เบิก
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      ปรับปรุงยอด
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      คงเหลือ
                    </TableCell>
                    <TableCell rowSpan={2} sx={headSx}>
                      หมายเหตุ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {['ซื้อ', 'ส่งคืน', 'รับจากผลิต'].map((label) => (
                      <TableCell key={label} sx={{ ...headSx, bgcolor: '#e8f5e9' }}>
                        {label}
                      </TableCell>
                    ))}
                    {['ขาย', 'รับคืน', 'ของแถม', 'โอนย้าย', 'ตัดเป็นเทสเตอร์'].map((label) => (
                      <TableCell key={label} sx={{ ...headSx, bgcolor: '#fff3e0' }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ border: '1px solid #eee', whiteSpace: 'nowrap' }}>
                        {Func.dateDisplay(row.move_date)}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', whiteSpace: 'nowrap' }}>
                        {row.ref_code}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', whiteSpace: 'nowrap' }}>
                        {row.code}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', minWidth: 180 }}>
                        {row.name}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', whiteSpace: 'nowrap' }}>
                        {row.unit_name}
                      </TableCell>
                      {cell(row.buy)}
                      {cell(row.send_back)}
                      {cell(row.prod)}
                      {cell(row.sale)}
                      {cell(row.take_back)}
                      {cell(row.gift)}
                      {cell(row.transfer)}
                      {cell(row.tester)}
                      {cell(row.adjust)}
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #eee', whiteSpace: 'nowrap', fontWeight: 600 }}
                      >
                        {Func.numberDisplay(Number(row.balance) || 0)}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', minWidth: 140 }}>
                        {row.remark}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t('element.show_record', { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                <Menu
                  anchorEl={show}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  open={Boolean(show)}
                  onClose={closeShow}
                  keepMounted
                >
                  {[10, 20, 100].map((num) => (
                    <MenuItem key={num} onClick={() => view({ ...find, page: 1, perPage: num })}>
                      {t('element.show_record', { num })}
                    </MenuItem>
                  ))}
                </Menu>
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

export default RpProductMovement;
